import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { auth, api } from "../lib/api";
import { toast } from "sonner";
import {
  Kanban,
  ChatTeardropDots,
  FilmStrip,
  Sparkle,
  SignOut,
  House,
  ShieldStar,
  BookOpenText,
  Target,
  Crown,
  Bank,
  Trophy,
  List,
  X,
  UserPlus,
  FilePdf,
  Gauge,
  ArrowsCounterClockwise,
  Calculator,
} from "@phosphor-icons/react";

const ITEMS = [
  { to: "/dashboard", label: "Pipeline", icon: Kanban, end: true },
  { to: "/dashboard/pipeline", label: "Carrier Pipeline", icon: Trophy },
  { to: "/dashboard/retention", label: "Retention", icon: ArrowsCounterClockwise },
  { to: "/dashboard/rollover", label: "Rollover Calculator", icon: Calculator },
  { to: "/dashboard/clarity", label: "Clarity Scores", icon: Gauge },
  { to: "/dashboard/callbacks", label: "Callbacks", icon: Kanban },
  { to: "/dashboard/pdf-exports", label: "Client PDF Exports", icon: FilePdf },
  { to: "/dashboard/audits", label: "Policy Audits", icon: BookOpenText },
  { to: "/dashboard/profiles", label: "Master Profiles", icon: Crown },
  { to: "/dashboard/admin", label: "Admin Users", icon: ShieldStar },
  { to: "/dashboard/wealth-for-life", label: "Wealth for Life Leads", icon: Bank },
  { to: "/dashboard/scenarios", label: "Goal Lab Pulse", icon: Target },
  { to: "/dashboard/playbook", label: "Sales Playbook", icon: BookOpenText },
  { to: "/dashboard/quiz", label: "Quiz Insights", icon: Sparkle },
  { to: "/dashboard/scripts", label: "Script Library", icon: ChatTeardropDots },
  { to: "/dashboard/studio", label: "Content Studio", icon: FilmStrip },
  { to: "/dashboard/closer-lab", label: "Closer Lab", icon: Target },
];

export default function DashboardLayout({ children }) {
  const nav = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showAddLead, setShowAddLead] = useState(false);

  const Sidebar = ({ mobile }) => (
    <>
      <div className="p-6 border-b border-exodus-border flex items-center justify-between">
        <div>
          <p className="font-display text-2xl gold-text leading-none">EXODUS</p>
          <p className="eyebrow text-exodus-muted mt-1">Advisor OS</p>
        </div>
        {mobile && (
          <button data-testid="dashboard-drawer-close" onClick={() => setDrawerOpen(false)}
            className="w-9 h-9 inline-flex items-center justify-center border border-exodus-border text-exodus-muted hover:text-exodus-gold hover:border-exodus-gold transition-colors">
            <X size={16} weight="bold" />
          </button>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-6 space-y-1">
        {ITEMS.map((i) => (
          <NavLink
            key={i.to}
            to={i.to}
            end={i.end}
            data-testid={`sidebar-${i.label.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => setDrawerOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                isActive
                  ? "bg-exodus-elevated text-exodus-gold border-l-2 border-exodus-gold"
                  : "text-exodus-muted hover:text-exodus-text hover:bg-exodus-elevated"
              }`
            }
          >
            <i.icon size={18} weight="regular" />
            {i.label}
          </NavLink>
        ))}
        <button
          data-testid="sidebar-add-lead"
          onClick={() => { setShowAddLead(true); setDrawerOpen(false); }}
          className="w-full flex items-center gap-3 px-4 py-3 mt-4 text-sm gold-text border border-exodus-gold hover:bg-exodus-gold hover:text-exodus-bg transition-colors"
        >
          <UserPlus size={18} /> Add Lead Manually
        </button>
      </nav>
      <div className="p-3 border-t border-exodus-border space-y-1">
        <button data-testid="sidebar-go-home" onClick={() => nav("/")}
          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-exodus-muted hover:text-exodus-text transition-colors">
          <House size={18} /> Public Site
        </button>
        <button data-testid="sidebar-logout"
          onClick={() => { auth.logout(); nav("/login"); }}
          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-exodus-muted hover:text-exodus-oxblood transition-colors">
          <SignOut size={18} /> Sign Out
        </button>
        <p className="px-4 text-xs text-exodus-muted/60 mt-3 truncate">{auth.email()}</p>
      </div>
    </>
  );

  return (
    <div data-testid="dashboard-layout" className="min-h-screen bg-exodus-bg text-exodus-text flex">
      {/* Desktop sidebar */}
      <aside data-testid="dashboard-sidebar" className="hidden lg:flex w-64 flex-col border-r border-exodus-border bg-exodus-surface">
        <Sidebar mobile={false} />
      </aside>

      {/* Mobile hamburger — always visible below lg */}
      <button
        data-testid="dashboard-hamburger"
        onClick={() => setDrawerOpen(true)}
        aria-label="Open menu"
        className="lg:hidden fixed top-4 left-4 z-40 w-11 h-11 inline-flex items-center justify-center bg-exodus-surface border border-exodus-gold-deep text-exodus-gold hover:bg-exodus-gold hover:text-exodus-bg transition-colors shadow-lg"
      >
        <List size={20} weight="bold" />
      </button>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div data-testid="dashboard-drawer" className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
          <button aria-label="Close menu" onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <aside className="absolute inset-y-0 left-0 w-[85%] max-w-sm bg-exodus-surface border-r border-exodus-gold-deep/40 shadow-2xl flex flex-col">
            <Sidebar mobile={true} />
          </aside>
        </div>
      )}

      <main className="flex-1 overflow-auto">{children}</main>

      {showAddLead && <AddLeadModal onClose={() => setShowAddLead(false)} />}
    </div>
  );
}

function AddLeadModal({ onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", goal: "", source: "manual-advisor-entry" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name || !form.email) { toast.error("Name and email are required."); return; }
    setSaving(true);
    try {
      await api.post("/leads/capture", form);
      toast.success(`Added ${form.name} to your leads.`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the lead.");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="add-lead-modal" className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-exodus-bg border border-exodus-gold max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <UserPlus size={20} className="text-exodus-gold" />
          <p className="eyebrow gold-text">Add lead manually</p>
        </div>
        <h3 className="font-display text-2xl mb-2">New lead into the pipeline.</h3>
        <p className="text-sm text-exodus-muted mb-6">Anyone you met offline, referred by a friend, or captured on a paper form — drop them here to keep everything in one place.</p>
        <div className="space-y-3">
          <input data-testid="add-lead-name" className="input-exodus" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input data-testid="add-lead-email" type="email" className="input-exodus" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input data-testid="add-lead-phone" type="tel" className="input-exodus" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <input data-testid="add-lead-goal" className="input-exodus" placeholder="Goal / interest (e.g. IUL retirement income)" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} />
          <input data-testid="add-lead-source" className="input-exodus" placeholder="Source (referral, event, cold call, etc.)" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} />
        </div>
        <div className="mt-6 flex items-center gap-3">
          <button data-testid="add-lead-submit" onClick={submit} disabled={saving} className="btn-gold flex-1 disabled:opacity-40">
            {saving ? "Saving…" : "Add to pipeline"}
          </button>
          <button data-testid="add-lead-cancel" onClick={onClose} className="text-sm text-exodus-muted hover:text-exodus-text px-4 py-3">Cancel</button>
        </div>
      </div>
    </div>
  );
}
